import { useCallback, useState, useRef, useEffect } from 'react';
import { MapPin, Loader2, X, Search, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { EUROPEAN_COUNTRIES, countryToFlag } from '../utils/countryFlags';

/**
 * Universal Address Form — used EVERYWHERE in the app.
 * Layout: Country (dropdown) → Postal Code → City → Address
 * Includes DAWA autocomplete for DK, Nominatim for the rest.
 *
 * Props:
 *   country, postalCode, city, address — current values
 *   onChange({ country, postal_code, city, address }) — called on any field change
 *   onGeocode({ latitude, longitude }) — optional, called when coordinates are resolved
 *   showCountry — whether to show country dropdown (default true)
 *   required — whether postal_code and city are required (default false)
 *   testIdPrefix — prefix for data-testid attributes
 */

const NOMINATIM_CODES = {
  DK: 'dk', DE: 'de', SE: 'se', NO: 'no', FI: 'fi', NL: 'nl',
  BE: 'be', FR: 'fr', ES: 'es', IT: 'it', PT: 'pt', AT: 'at',
  CH: 'ch', PL: 'pl', CZ: 'cz', GB: 'gb', IE: 'ie', IS: 'is',
  HR: 'hr', HU: 'hu', RO: 'ro', BG: 'bg', GR: 'gr', SK: 'sk',
  SI: 'si', LT: 'lt', LV: 'lv', EE: 'ee', LU: 'lu',
};

const PLACEHOLDERS = {
  DK: { postal: 'f.eks. 2100', city: 'f.eks. Aarhus', address: 'Gadenavn og nummer' },
  DE: { postal: 'z.B. 10115', city: 'z.B. Berlin', address: 'Straße und Hausnummer' },
  SE: { postal: 't.ex. 11120', city: 't.ex. Stockholm', address: 'Gatuadress' },
  NO: { postal: 'f.eks. 0150', city: 'f.eks. Oslo', address: 'Gateadresse' },
  NL: { postal: 'bijv. 1012', city: 'bijv. Amsterdam', address: 'Straat en nummer' },
  GB: { postal: 'e.g. SW1A 1AA', city: 'e.g. London', address: 'Street and number' },
  ES: { postal: 'p.ej. 28001', city: 'p.ej. Madrid', address: 'Calle y numero' },
  IT: { postal: 'es. 00100', city: 'es. Roma', address: 'Via e numero' },
  FR: { postal: 'ex. 75001', city: 'ex. Paris', address: 'Rue et numero' },
  PL: { postal: 'np. 00-001', city: 'np. Warszawa', address: 'Ulica i numer' },
  AT: { postal: 'z.B. 1010', city: 'z.B. Wien', address: 'Straße und Hausnummer' },
  CH: { postal: 'z.B. 8001', city: 'z.B. Zurich', address: 'Strasse und Nummer' },
};

const getPlaceholder = (country, field) => {
  return PLACEHOLDERS[country]?.[field] || PLACEHOLDERS.DK[field];
};

const inputClass = 'w-full h-10 px-3 text-sm rounded-lg bg-zinc-950/50 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/30 transition-colors';
const labelClass = 'text-zinc-400 text-[11px] uppercase tracking-wider font-bold mb-1 block';

const AddressForm = ({
  country = 'DK',
  postalCode = '',
  city = '',
  address = '',
  onChange,
  onGeocode,
  showCountry = true,
  required = false,
  testIdPrefix = 'addr',
}) => {
  const { t } = useLanguage();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cityAutoFilled, setCityAutoFilled] = useState(false);
  const [postalLooking, setPostalLooking] = useState(false);
  const debounceRef = useRef(null);
  const postalDebounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset suggestions when country changes
  useEffect(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setCityAutoFilled(false);
  }, [country]);

  const handleChange = useCallback((field, value) => {
    const update = { country, postal_code: postalCode, city, address };
    update[field] = value;
    onChange(update);
  }, [country, postalCode, city, address, onChange]);

  // ─── Postal code → City auto-lookup ───
  // DAWA for DK, Zippopotam.us for rest of Europe
  // Only fills city if it's empty or was auto-filled last time

  // Trigger postal lookup when postal code changes
  const handlePostalChange = useCallback((newPostal) => {
    handleChange('postal_code', newPostal);
    clearTimeout(postalDebounceRef.current);
    if (newPostal.length >= 3) {
      postalDebounceRef.current = setTimeout(async () => {
        setPostalLooking(true);
        try {
          let resolvedCity = null;
          if (country === 'DK') {
            const cleanPostal = newPostal.replace(/\s/g, '');
            if (/^\d{4}$/.test(cleanPostal)) {
              const res = await fetch(`https://api.dataforsyningen.dk/postnumre/${cleanPostal}`);
              if (res.ok) {
                const data = await res.json();
                resolvedCity = data?.navn || null;
              }
            }
          } else {
            const ccLower = (country || '').toLowerCase();
            if (ccLower) {
              const res = await fetch(`https://api.zippopotam.us/${ccLower}/${encodeURIComponent(newPostal)}`);
              if (res.ok) {
                const data = await res.json();
                resolvedCity = data?.places?.[0]?.['place name'] || null;
              }
            }
          }
          if (resolvedCity && (!city || cityAutoFilled)) {
            onChange({ country, postal_code: newPostal, city: resolvedCity, address });
            setCityAutoFilled(true);
          }
        } catch { /* fail silently — user can always type manually */ }
        finally { setPostalLooking(false); }
      }, 500);
    }
  }, [country, city, address, cityAutoFilled, handleChange, onChange]);

  // When user manually types in city, mark it as NOT auto-filled
  const handleCityChange = useCallback((val) => {
    setCityAutoFilled(false);
    handleChange('city', val);
  }, [handleChange]);

  // Address autocomplete
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      if (country === 'DK') {
        const res = await fetch(`https://api.dataforsyningen.dk/adresser/autocomplete?q=${encodeURIComponent(query)}&per_page=5`);
        const data = await res.json();
        setSuggestions(data.map(item => ({
          text: item.tekst,
          address: item.adresse?.vejnavn ? `${item.adresse.vejnavn} ${item.adresse.husnr || ''}`.trim() : item.tekst,
          postalCode: item.adresse?.postnr || '',
          city: item.adresse?.postnrnavn || '',
        })));
      } else {
        const cc = NOMINATIM_CODES[country] || '';
        const countryParam = cc ? `&countrycodes=${cc}` : '';
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}${countryParam}&format=json&addressdetails=1&limit=5`,
          { headers: { 'Accept-Language': 'da,en' } }
        );
        const data = await res.json();
        setSuggestions(data.map(item => ({
          text: item.display_name?.split(',').slice(0, 3).join(',') || item.display_name,
          address: item.address?.road ? `${item.address.road} ${item.address.house_number || ''}`.trim() : '',
          postalCode: item.address?.postcode || '',
          city: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || '',
          lat: item.lat,
          lon: item.lon,
        })));
      }
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
    setLoading(false);
  }, [country]);

  const handleAddressInput = (val) => {
    handleChange('address', val);
    clearTimeout(debounceRef.current);
    if (val.length >= 2) {
      debounceRef.current = setTimeout(() => fetchSuggestions(val), country === 'DK' ? 250 : 400);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (s) => {
    onChange({
      country,
      postal_code: s.postalCode || postalCode,
      city: s.city || city,
      address: s.address || address,
    });
    if (s.lat && s.lon && onGeocode) {
      onGeocode({ latitude: parseFloat(s.lat), longitude: parseFloat(s.lon) });
    }
    // For DAWA, geocode the selected address
    if (country === 'DK' && s.postalCode && onGeocode) {
      const q = s.address ? `${s.address}, ${s.postalCode} ${s.city}, Denmark` : `${s.postalCode}, Denmark`;
      fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`, { headers: { 'Accept-Language': 'da' } })
        .then(r => r.json())
        .then(data => {
          if (data?.[0]) onGeocode({ latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) });
        })
        .catch(() => {});
    }
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Manual geocode button
  const handleGeocode = async () => {
    if (!postalCode && !address) return;
    setLoading(true);
    try {
      const cc = NOMINATIM_CODES[country] || '';
      const parts = [address, postalCode, city].filter(Boolean).join(', ');
      const countryParam = cc ? `&countrycodes=${cc}` : '';
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(parts)}${countryParam}&format=json&limit=1&addressdetails=1`,
        { headers: { 'Accept-Language': 'da,en' } }
      );
      const data = await res.json();
      if (data?.[0]) {
        const item = data[0];
        const resolvedCity = item.address?.city || item.address?.town || item.address?.village || '';
        if (resolvedCity && !city) handleChange('city', resolvedCity);
        if (onGeocode) onGeocode({ latitude: parseFloat(item.lat), longitude: parseFloat(item.lon) });
      }
    } catch { /* silently fail */ }
    setLoading(false);
  };

  return (
    <div className="space-y-3" ref={wrapperRef} data-testid={`${testIdPrefix}-form`}>
      {/* Country */}
      {showCountry && (
        <div>
          <label className={labelClass}>
            {t('register.country') || 'Land'} <span className="text-red-400">*</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xl leading-none">{countryToFlag(country)}</span>
            <select
              value={country}
              onChange={(e) => onChange({ country: e.target.value, postal_code: postalCode, city, address })}
              className={`${inputClass} flex-1`}
              data-testid={`${testIdPrefix}-country`}
            >
              <option value="">-- {t('register.selectCountry') || 'Vaelg land'} --</option>
              {EUROPEAN_COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{countryToFlag(c.code)} {c.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Postal Code */}
      <div>
        <label className={labelClass}>
          {t('clubs.postalCode') || 'Postnummer'} {required && <span className="text-red-400">*</span>}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={postalCode}
            onChange={(e) => handlePostalChange(e.target.value)}
            placeholder={getPlaceholder(country, 'postal')}
            className={`${inputClass} flex-1`}
            required={required}
            data-testid={`${testIdPrefix}-postal-code`}
          />
          <button
            type="button"
            onClick={handleGeocode}
            disabled={loading || (!postalCode && !address)}
            className="px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors disabled:opacity-40 flex-shrink-0"
            title={t('clubs.findOnMap') || 'Find paa kort'}
            data-testid={`${testIdPrefix}-geocode-btn`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* City — auto-filled from postal code, always manually editable */}
      <div>
        <label className={labelClass}>
          {t('clubs.city') || 'By'} {required && <span className="text-red-400">*</span>}
        </label>
        <div className="relative">
          <input
            type="text"
            value={city}
            onChange={(e) => handleCityChange(e.target.value)}
            placeholder={getPlaceholder(country, 'city')}
            className={`${inputClass} ${cityAutoFilled ? 'pr-8' : ''}`}
            required={required}
            data-testid={`${testIdPrefix}-city`}
          />
          {postalLooking && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-400 animate-spin" />
          )}
          {!postalLooking && cityAutoFilled && city && (
            <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-400" />
          )}
        </div>
      </div>

      {/* Address with autocomplete */}
      <div className="relative">
        <label className={labelClass}>
          {t('clubs.address') || 'Adresse'}
        </label>
        <div className="relative">
          <input
            type="text"
            value={address}
            onChange={(e) => handleAddressInput(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            placeholder={getPlaceholder(country, 'address')}
            className={`${inputClass} pl-9`}
            data-testid={`${testIdPrefix}-address`}
            autoComplete="off"
          />
          <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />}
          {!loading && address && (
            <button
              type="button"
              onClick={() => { handleChange('address', ''); setSuggestions([]); setShowSuggestions(false); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              data-testid={`${testIdPrefix}-clear-address`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Autocomplete dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-zinc-900 border border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-testid={`${testIdPrefix}-suggestions`}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                className="w-full text-left px-3 py-2.5 text-sm text-white hover:bg-emerald-500/10 transition-colors flex items-start gap-2 border-b border-white/5 last:border-0"
                data-testid={`${testIdPrefix}-suggestion-${i}`}
              >
                <MapPin className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="block truncate">{s.text}</span>
                  {s.postalCode && (
                    <span className="text-xs text-zinc-500">{s.postalCode} {s.city}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressForm;
